/* Nardocar — Typesense-powered header search.
 * Replaces the native <predictive-search> on the header (the Liquid wrapper is
 * renamed to <nardo-header-search>, so the native component never binds).
 *  • On the /search page: the header input live-drives the InstantSearch
 *    results (via window.nardoSearchSetQuery exposed by typesense-search.js).
 *  • Everywhere else: a debounced Typesense query renders an autocomplete
 *    dropdown of product suggestions; submitting goes to /search?q=. */
(function () {
  "use strict";

  var cfg = window.nardoSearch || {};
  var SEARCH_URL = cfg.searchUrl || "/search";

  function onSearchPage() {
    var here = location.pathname.replace(/\/$/, "");
    return here === SEARCH_URL.replace(/\/$/, "");
  }

  var L = cfg.labels || {};

  // Replay the shop's money_format so prices match the native cards (see
  // typesense-search.js for the same two lines).
  var MONEY_FORMAT = String(cfg.moneyFormat || "{{amount_no_decimals_with_comma_separator}} kr")
    .replace(/<[^>]*>/g, "");
  var moneyDigits = MONEY_FORMAT.indexOf("no_decimals") > -1 ? 0 : 2;
  var num = new Intl.NumberFormat(cfg.locale || "da-DK", {
    minimumFractionDigits: moneyDigits,
    maximumFractionDigits: moneyDigits,
  });
  function money(n) { return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, num.format(n)); }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Gemt bil i Min Garage: samme tag som /search og collection-siderne (variant, ellers model, ellers mærke).
  // Bilens dele først, derefter kun universelle varer — aldrig dele til andre biler (window.ncFit).
  function fitTag() {
    var c = null;
    try { c = ncVehicle.load(); } catch (e) {}
    if (c && /^\d+$/.test(String(c.typeId || ""))) return "vehicle_type_id:" + c.typeId;
    if (c && /^\d+$/.test(String(c.modelId || ""))) return "model_id:" + c.modelId;
    if (c && c.makeName) return "make:" + c.makeName;
    return null;
  }
  function fits(doc, tag) { return !!tag && !!doc && window.ncFit.fits(doc, tag); }

  // ── Typesense REST search (CORS is * on the live instance) ────────────
  function tsSearch(query, tag) {
    // Same routing as typesense-search.js: part numbers to products_partno, unchanged.
    var partno = (window.ncLooksLikePartNumber || function () { return false; })(query);
    var url =
      "https://" + cfg.host + "/collections/" + encodeURIComponent(cfg.collection) +
      // Brand typos fixed first on text searches (window.ncFixQuery, config snippet).
      "/documents/search?q=" + encodeURIComponent(partno ? query : (window.ncFixQuery || String)(query)) +
      // query_by, weights and typos live in the preset (nardo-typesense src/search/relevance.ts).
      "&preset=" + (partno ? "products_partno" : "products_text") +
      "&x-typesense-user-id=" + encodeURIComponent(window.ncTsUserId ? window.ncTsUserId() : "anonymous") +
      "&group_by=product_id&group_limit=1&per_page=6" +
      "&filter_by=" + encodeURIComponent("visibility:=all && tags:!=[`skjult`]" + (tag ? " && " + window.ncFit.scope(tag) : "")) +
      (tag ? "&sort_by=" + encodeURIComponent(window.ncFit.first(tag) + ",_text_match:desc") : "");
    // Typesense down/slow/erroring: give up after 3 s; the catch below keeps the dropdown closed.
    var signal = window.AbortSignal && AbortSignal.timeout ? AbortSignal.timeout(3000) : undefined;
    return fetch(url, { headers: { "X-TYPESENSE-API-KEY": cfg.searchKey }, signal: signal }).then(function (r) {
      if (!r.ok) throw new Error("typesense " + r.status);
      return r.json();
    });
  }

  function docsFrom(data) {
    var out = [];
    if (data && data.grouped_hits) {
      data.grouped_hits.forEach(function (g) { if (g.hits && g.hits[0]) out.push(g.hits[0].document); });
    } else if (data && data.hits) {
      data.hits.forEach(function (h) { out.push(h.document); });
    }
    return out;
  }

  // Second line that tells look-alike suggestions apart ("KW V3 … Golf 7" x4): årgang and
  // part number first (always unique, survives the mobile ellipsis), then akseltryk and
  // motor from the indexed card_specs. Akseltryk is always a maximum: "op til" dropped, as on the cards.
  function metaLine(h) {
    var specs = [];
    try { specs = JSON.parse(h.card_specs || "[]"); } catch (e) {}
    function spec(label) {
      for (var i = 0; i < specs.length; i++) if (specs[i] && specs[i].label === label && specs[i].value) return String(specs[i].value);
      return "";
    }
    var pn = h.mpn || h.sku;
    var axle = spec("Akseltryk for:").replace(/^op til\s+/i, "");
    return [
      spec("Årgang:"),
      pn ? (L.sku || "") + " " + pn : "",
      axle ? "Akseltryk for: " + axle : "",
      spec("Modelvariant:"),
    ].filter(Boolean).join(" · ");
  }

  // ── autocomplete dropdown ─────────────────────────────────────────────
  function renderDropdown(container, docs, query, tag) {
    if (!docs.length) {
      container.innerHTML =
        '<div class="nardo-ac-empty">' + esc(String(L.noResults || "").replace("[terms]", query)) + "</div>";
      return;
    }
    // Bilens dele står forrest (sort_by _eval): en overskrift over dem og en skillelinje
    // med egen overskrift før resten. Intet mærke på det enkelte forslag.
    var anyFits = fits(docs[0], tag);
    function group(cls, label) {
      return '<li class="nardo-ac-group ' + cls + '" role="presentation">' + esc(label) + "</li>";
    }
    var items = docs.map(function (h, i) {
      var head = !anyFits ? ""
        : i === 0 ? group("nardo-ac-group--match", L.groupMatch)
        : !fits(h, tag) && fits(docs[i - 1], tag) ? group("nardo-ac-group--rest", L.groupRest)
        : "";
      var img = h.featured_image || (h.images && h.images[0]) || "";
      return head + (
        '<li class="nardo-ac-item" role="option">' +
          '<a class="nardo-ac-link" href="' + ncVehicle.url('/products/' + encodeURIComponent(h.handle)) + '">' +
            '<span class="nardo-ac-media">' +
              (img ? '<img loading="lazy" src="' + esc(img) + '" alt="" width="48" height="48">' : "") +
            "</span>" +
            '<span class="nardo-ac-info">' +
              '<span class="nardo-ac-title">' + esc(h.title) + "</span>" +
              '<span class="nardo-ac-meta">' + esc(metaLine(h)) + "</span>" +
              '<span class="nardo-ac-price">' + esc(money(h.price)) + "</span>" +
            "</span>" +
          "</a>" +
        "</li>"
      );
    }).join("");
    // Passer det første forslag ikke, passer intet.
    var car = null;
    try { car = tag && !anyFits ? ncVehicle.load() : null; } catch (e) {}
    container.innerHTML =
      (car && car.typeName && L.fitSearchNotice
        ? '<div class="nardo-ac-fit-none">' + esc(L.fitSearchNotice).replace(/\[car\]/g, "<strong>" + esc(car.typeName) + "</strong>") + "</div>"
        : "") +
      '<ul class="nardo-ac-list" role="listbox">' + items + "</ul>" +
      '<a class="nardo-ac-all" href="' + SEARCH_URL + "?q=" + encodeURIComponent(query) + '">' +
        esc(String(L.searchFor || "").replace("[terms]", query)) + "</a>";
  }

  function initAutocomplete(root) {
    var input = root.querySelector('input[type="search"]');
    var results = root.querySelector("[data-predictive-search]");
    if (!input || !results) return;

    var open = function () { root.classList.add("nardo-ac-open"); input.setAttribute("aria-expanded", "true"); };
    var close = function () { root.classList.remove("nardo-ac-open"); input.setAttribute("aria-expanded", "false"); };

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { close(); return; }
      var tag = fitTag();
      tsSearch(q, tag).then(function (data) {
        if (input.value.trim() !== q) return; // stale
        // Andre bilers varer, som kun er knyttet via et numerisk fit-id (kan ikke filtreres i Typesense).
        renderDropdown(results, docsFrom(data).filter(function (d) { return !tag || window.ncFit.keep(d, tag); }), q, tag);
        open();
      }).catch(function () { close(); });
    }, 180);

    input.addEventListener("input", run);
    input.addEventListener("focus", function () { if (input.value.trim().length >= 2) run(); });
    input.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
    document.addEventListener("click", function (e) { if (!root.contains(e.target)) close(); });
    // submit falls through to the form's native action="/search?q=".
  }

  // ── /search live filtering ────────────────────────────────────────────
  function initLiveFilter(root) {
    var input = root.querySelector('input[type="search"]');
    var form = root.querySelector("form");
    if (!input) return;

    var params = new URLSearchParams(location.search);
    if (params.get("q") && !input.value) input.value = params.get("q");

    var apply = debounce(function () {
      if (typeof window.nardoSearchSetQuery === "function") {
        window.nardoSearchSetQuery(input.value.trim());
      }
    }, 180);
    input.addEventListener("input", apply);
    if (form) {
      form.addEventListener("submit", function (e) {
        if (typeof window.nardoSearchSetQuery === "function") {
          e.preventDefault(); // already live-filtering; no full reload
          window.nardoSearchSetQuery(input.value.trim());
        }
      });
    }
  }

  function init() {
    var roots = document.querySelectorAll("nardo-header-search");
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.nardoBound) return;
      root.dataset.nardoBound = "1";
      if (onSearchPage()) initLiveFilter(root);
      else initAutocomplete(root);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

/* Megamenu title layout (display/align-items/gap/overflow/white-space/text-overflow)
   used to be forced here with el.style.setProperty(..., 'important') on load and on
   every hover. Collapsed into assets/nardocar-mega-menu-v5.css (NAR-50) — the CSS rule
   there carries the same six declarations at a specificity that wins outright, so
   dynamically shown menus get them for free. */

